import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import Breadcrumb from "./Breadcrumb";
import { getSegmentId, getSegmentItems } from "../utils/segmentFlow";
import { getActiveCartSegmentInfo, getActiveCartStoreName } from "../utils/cartSegmentGuard";
import {
  getNavigationIdFromSlug, getNavigationLabelFromSlug, getNavigationSlug,
  getNavigationValue as value, readShoppingNavigation, rememberShoppingNavigation,
} from "../utils/shoppingNavigation";

const STORE_IDS = ["business_segment_id", "businessSegmentId", "store_id", "storeId", "id", "branch_id"];
const SEGMENT_IDS = ["segment_id", "segmentId", "segment"];
const STORE_NAMES = ["title", "name", "store_name", "business_segment_name", "business_name"];
const EMPTY_OBJECT = {};
const object = (input) => {
  try {
    const parsed = typeof input === "string" ? JSON.parse(input) : input;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch { return {}; }
};

export default function ShoppingBreadcrumb({ page, segment, store, product, segmentId, businessSegmentId, storeId, loading = false }) {
  const location = useLocation();
  const params = useParams();
  const common = useSelector((state) => state.common || EMPTY_OBJECT);
  const cart = useSelector((state) => state.cart || EMPTY_OBJECT);
  const auth = useSelector((state) => state.auth || EMPTY_OBJECT);
  const state = location.state || {};
  const isCart = page === "cart";
  const isProduct = page === "product";
  const cartStore = object(cart.data?.business_segment);
  const cartInfo = getActiveCartSegmentInfo(cart);
  const products = [cart.serverCartSummary?.product_details, cart.data?.product_details, cart.cartItems]
    .find(Array.isArray) || [];
  const hasProducts = products.length > 0;
  const cartReady = isCart && !loading && hasProducts;
  const businessId = isCart
    ? (cartReady ? value(cartInfo, ["business_segment_id"]) || value(cartStore, STORE_IDS) : "")
    : String(businessSegmentId || state.businessSegmentId || value(store || state.store, STORE_IDS) ||
      (page === "shop" ? getNavigationIdFromSlug(params.storeSlug) : params.storeId || getNavigationIdFromSlug(params.categorySlug)) || "");
  const storageKey = `rghtnow:shopping-navigation:${value(auth.userData, ["id", "user_id"]) || "guest"}:${value(auth.user_location, ["merchant_id", "merchantId"]) || auth.merchant_id || "default"}`;
  const saved = readShoppingNavigation(storageKey);
  // Never use another store's browsing history to build the active cart's trail.
  const cached = saved.stores?.[businessId] || {};
  const matchingStore = getSegmentItems(common.storesData).find((item) => value(item, STORE_IDS) === businessId);
  const routeStore = store || state.store;
  const selectedStore = isCart ? (!value(cartStore, STORE_IDS) || value(cartStore, STORE_IDS) === businessId ? cartStore : {}) :
    (routeStore && (!value(routeStore, STORE_IDS) || value(routeStore, STORE_IDS) === businessId) ? routeStore : {});
  const activeSegmentId = isCart
    ? (cartReady ? value(cartInfo, SEGMENT_IDS) || value(cartStore, SEGMENT_IDS) || value(matchingStore, SEGMENT_IDS) || cached.segmentId || "" : "")
    : String(segmentId || state.segmentId || (page === "stores" ? params.segmentId || new URLSearchParams(location.search).get("segment_id") : "") ||
      value(selectedStore, SEGMENT_IDS) || value(matchingStore, SEGMENT_IDS) || cached.segmentId || "");
  const selectedSegment = [segment, state.segment, ...getSegmentItems(common.servicesData)]
    .find((item) => item && String(getSegmentId(item)) === activeSegmentId);
  const segmentName = value(selectedSegment, ["title", "name", "segment_name"]) || saved.segments?.[activeSegmentId] || cached.segmentName || "";
  const storeName = (isCart ? getActiveCartStoreName(cart, common.storesData) : "") ||
    value(selectedStore, STORE_NAMES) || value(matchingStore, STORE_NAMES) || cached.storeName ||
    getNavigationLabelFromSlug(page === "shop" ? params.storeSlug : page === "categories" ? params.categorySlug : "");
  const storeSlug = (page === "shop" ? params.storeSlug : page === "categories" ? params.categorySlug : isProduct ? state.storeSlug : "") ||
    cached.storeSlug || (businessId ? getNavigationSlug(storeName, businessId) : "");
  const selectedStoreId = String((isCart ? "" : storeId || state.storeId) || value(selectedStore, ["store_id", "storeId"]) || value(matchingStore, ["store_id", "storeId"]) || cached.storeId || businessId);
  const category = object(state.category);
  const categoryId = page === "shop"
    ? String(state.parentCategoryId || state.categoryId || value(category, ["category_id", "categoryId", "id"]) || getNavigationIdFromSlug(params.categorySlug) || "")
    : isProduct ? String(state.parentCategoryId || state.categoryId || value(category, ["category_id", "categoryId", "id"]) || cached.categoryId || "")
    : isCart && businessId ? cached.categoryId || "" : "";
  const categoryName = page === "shop"
    ? value(category, ["title", "name", "category_name"]) || getNavigationLabelFromSlug(params.categorySlug)
    : isProduct ? value(category, ["title", "name", "category_name"]) || cached.categoryName || getNavigationLabelFromSlug(state.categorySlug)
    : isCart && businessId ? cached.categoryName || "" : "";
  const categorySlug = page === "shop" ? params.categorySlug || "" : isProduct ? state.categorySlug || cached.categorySlug || "" : isCart && businessId ? cached.categorySlug || "" : "";
  const context = JSON.stringify({
    businessSegmentId: businessId, storeId: selectedStoreId, storeName, storeSlug,
    segmentId: activeSegmentId, segmentName,
    categoryId, categoryName, categorySlug,
  });

  useEffect(() => {
    if (!isCart) rememberShoppingNavigation(storageKey, JSON.parse(context));
  }, [context, isCart, storageKey]);

  const linkState = {
    segment: selectedSegment,
    segmentId: activeSegmentId,
    businessSegmentId: businessId,
    storeId: selectedStoreId,
    store: { ...matchingStore, ...selectedStore, business_segment_id: businessId, store_id: selectedStoreId, segment_id: activeSegmentId, title: storeName },
  };
  const items = [{ label: "Home", to: "/home" }];
  if (page === "stores" || businessId || activeSegmentId) {
    items.push({ label: segmentName || "Stores", to: activeSegmentId ? `/stores/${encodeURIComponent(activeSegmentId)}` : "/stores", state: { segment: selectedSegment } });
  }
  if (businessId) {
    items.push({ label: storeName ? `${storeName} Categories` : "Categories", to: `/category/${encodeURIComponent(storeSlug)}`, state: linkState });
  } else if (page === "categories") {
    items.push({ label: "Categories" });
  }
  if (page === "shop" || ((isCart || isProduct) && businessId && categorySlug)) {
    items.push({
      label: categoryName || "Shop",
      to: storeSlug ? `/shop/${encodeURIComponent(storeSlug)}/${encodeURIComponent(categorySlug)}` : undefined,
      state: { ...linkState, category: { category_id: categoryId, title: categoryName }, categoryId, parentCategoryId: categoryId },
    });
  }
  if (isCart) items.push({ label: "My basket" });
  if (isProduct) items.push({ label: product?.title || product?.name || product?.product_name || "Product details" });
  return <Breadcrumb items={items} activeColor="#2ECC71" />;
}
